// pt-BR da onda 5 (decisão D23); completo para toda chave de en-wave5.ts (checado pelo tipo). Linguagem simples:
// quem decide nem sempre é da área técnica.
import type { enWave5 } from "./en-wave5.ts";

export const ptBRWave5: Record<keyof typeof enWave5, string> = {
  // Aguardando decisão humana (Kanban)
  "approval.columnHint": "O conselho decide tudo o que pode. Estas tarefas precisam da sua resposta para continuar.",
  "approval.loadFailed": "Não foi possível carregar as perguntas pendentes",
  "approval.waitingFor": "Esperando há {duration}",
  "approval.options": "Respostas",
  "approval.recommended": "recomendada",
  "approval.replyCode": "Código de resposta",
  "approval.replyHint": "Para responder pelo WhatsApp ou Telegram, envie “{example}”.",
  "approval.glossary": "O que significam os termos",
  "approval.note": "Observação para a equipe",
  "approval.missing": "A pergunta desta tarefa ainda não está disponível.",
  "approval.decided": "Resposta registrada para a tarefa #{number}: {option}.",
  "approval.runNotStarted": "A resposta foi registrada, mas o trabalho não recomeçou: {reason}",
  "approval.alreadyDecided": "A tarefa #{number} já foi respondida por outra pessoa. O quadro foi atualizado.",
  "approval.needsDecision": "Precisa da sua decisão",
  "approval.yourDecision": "Sua decisão",
  "approval.haltedNotice.paused": "O projeto está pausado. Esta opção volta a tarefa para produção; retome o projeto para aplicá-la.",
  "approval.haltedNotice.draining": "O projeto está em drenagem. Esta opção volta a tarefa para produção; retome o projeto para aplicá-la.",
  "approval.haltedNotice.stopped": "O projeto está parado. Esta opção volta a tarefa para produção; retome o projeto para aplicá-la.",
  "approval.resumeProject": "Retomar projeto",
  "approval.resumeSuccess": "Projeto retomado.",

  // Contabilidade de produção
  "production.title": "Produção",
  "production.projectTitle": "Produção",
  "production.loadFailed": "Não foi possível carregar os números de produção",
  "production.productionTime": "Tempo produzindo",
  "production.waitingHuman": "Esperando uma pessoa",
  "production.blockedTime": "Bloqueada",
  "production.attempts": "Tentativas",
  "production.attemptsValue": "{ok} certas · {failed} com falha",
  "production.tokens": "Tokens (entrada · saída)",
  "production.tokensValue": "{input} · {output}",
  "production.cost": "Custo conhecido",
  "production.unknownCost": "custo desconhecido em {n} chamadas",
  "production.models": "Modelos usados",
  "production.agents": "Agentes",
  "production.noModels": "Nenhuma chamada de modelo registrada ainda.",
  "production.noAgents": "Nenhuma tentativa de agente registrada ainda.",
  "production.tasksDone": "Tarefas prontas",
  "production.doneOf": "{done} de {total}",
  "production.tasksWorking": "Em produção agora",
  "production.tasksAwaitingHuman": "Esperando uma pessoa",
  "production.tasksBlocked": "Bloqueadas",
  "production.elapsed": "Tempo desde o primeiro início",
  "production.col.model": "Modelo",
  "production.col.agent": "Agente",
  "production.col.calls": "Chamadas",
  "production.col.attempts": "Tentativas",
  "production.col.errorRate": "Erros",
  "production.col.successRate": "Acertos",
  "production.col.tokens": "Tokens (entrada · saída)",
  "production.col.cost": "Custo conhecido",
  "production.col.shareDone": "% das tarefas prontas",
  "production.col.shareWork": "% do tempo de trabalho",

  // Conselho
  "council.title": "Conselho",
  "council.loadFailed": "Não foi possível carregar as análises do conselho",
  "council.none": "O conselho ainda não analisou esta tarefa.",
  "council.applied": "O que aconteceu: {action}",
  "council.singleCall": "Cinco perspectivas de uma única chamada ao modelo, não agentes independentes.",
  "council.verdict.approve_once": "Aprovado desta vez",
  "council.verdict.approve_rule": "Aprovado como regra",
  "council.verdict.deny": "Negado",
  "council.verdict.clarify": "Precisa de esclarecimento",
  "council.verdict.escalate_human": "Enviado para uma pessoa",
  "council.action.unblocked_for_retry": "desbloqueada para tentar de novo",
  "council.action.paid_escalation_approved": "aprovada uma tentativa mais forte (paga)",
  "council.action.escalated_to_human": "pedido para uma pessoa decidir",
  "council.action.kept_blocked": "mantida bloqueada",

  // Visão geral do projeto
  "insights.title": "Produção e decisões",
  "insights.refresh": "Atualizar números",
  "decisions.title": "Decisões humanas",
  "decisions.pending": "Esperando resposta",
  "decisions.decided": "Respondidas",
  "decisions.timeToDecide": "Tempo para responder (mediana · 90%)",
  "decisions.timeToResume": "Tempo até voltar à produção (mediana · 90%)",
  "decisions.medianP90": "{median} · {p90}",
  "decisions.council": "{escalated} de {reviews} análises do conselho precisaram de uma pessoa.",
  "decisions.byChannel": "Respondidas por:",
  "decisions.hint": "90% quer dizer que nove em cada dez respostas levaram no máximo esse tempo.",
  "channel.web": "Painel",
  "channel.whatsapp": "WhatsApp",
  "channel.telegram": "Telegram",
  "channel.email": "E-mail",
  "channel.api": "API",
  "planning.title": "Planejamento",
  "planning.start": "Planejar com agentes",
  "planning.hint": "Os agentes leem o PRD mais recente, dividem em tarefas, escolhem o agente de cada uma e colocam em ordem, para nenhuma tarefa começar antes daquilo de que depende.",
  "planning.requested": "Planejamento pedido. As tarefas aparecem no quadro quando terminar.",
  "planning.none": "Nenhum planejamento ainda.",
  "planning.tasksCreated": "{n} tarefas criadas.",
  "planning.state.requested": "Esperando começar",
  "planning.state.running": "Planejando",
  "planning.state.completed": "Concluído",
  "planning.state.failed": "Falhou",
  "planning.mode.agent": "Planejado por um agente",
  "planning.mode.rules_fallback": "Planejado pelas regras básicas (agente indisponível)",
  "parallelism.title": "Trabalho em paralelo",
  "parallelism.summary": "Recomendado: {recommended} tarefas ao mesmo tempo (até {max} poderiam andar juntas; limite do projeto {limit}).",
  "parallelism.facts": "{waves} rodadas · maior sequência de tarefas dependentes: {critical} · esperando dependências: {blocked}",
  "parallelism.none": "Nenhuma tarefa aberta para organizar.",
  "parallelism.wave": "Rodada {n}:",

  // Página de agentes
  "agents.tab.leaderboard": "Desempenho",
  "agents.tab.playbooks": "Manuais",
  "agents.subtitle.leaderboard": "Quais agentes e modelos entregam e quais precisam de atenção.",
  "agents.subtitle.playbooks": "As instruções que cada agente da plataforma segue. Somente leitura.",
  "leaderboard.hint": "Tentativas concluídas no período. Um item é sinalizado quando tem tentativas suficientes e erra mais do que acerta.",
  "leaderboard.emptyTitle": "Nenhuma tentativa concluída neste período",
  "leaderboard.emptyBody": "A classificação aparece quando os agentes terminarem algum trabalho.",
  "leaderboard.agents": "Agentes",
  "leaderboard.models": "Modelos",
  "leaderboard.col.status": "Avaliação",
  "leaderboard.underperforming": "Abaixo do esperado",
  "leaderboard.suggestAgent": "Melhore este agente ou troque o modelo dele.",
  "leaderboard.suggestModel": "Considere trocar este modelo.",
  "leaderboard.ok": "OK",
  "playbooks.hint": "Os manuais são mantidos junto com a plataforma; aparecem aqui para todos saberem como cada agente trabalha.",
  "playbooks.emptyTitle": "Nenhum manual disponível",
  "playbooks.instructions": "Ver instruções",
  "playbooks.version": "versão {version}",
  "playbooks.evaluation": "Como o resultado é conferido",

  // Configurações: contatos e notificações
  "settings.tab.notifications": "Contatos e notificações",
  "contact.title": "Meus contatos",
  "contact.intro": "Onde pedimos suas decisões. WhatsApp ou Telegram vêm primeiro; se não houver, um e-mail diário lista o que está esperando, com um link que abre o quadro sem senha.",
  "contact.channels": "Como falar comigo",
  "contact.receive.whatsapp": "Receber perguntas no WhatsApp",
  "contact.receive.telegram": "Receber perguntas no Telegram",
  "contact.linked.whatsapp": "Vinculado a {id}",
  "contact.linked.telegram": "Vinculado ao chat {id}",
  "contact.notLinked": "{channel} ainda não está vinculado.",
  "contact.unlink": "Desvincular {channel}",
  "contact.unlinked": "{channel} desvinculado.",
  "contact.linkGenerate": "Gerar código de vínculo",
  "contact.linkCode": "Seu código:",
  "contact.linkHow.whatsapp": "Envie {code} do seu celular para o WhatsApp da plataforma em até {n} minutos. O número que enviar fica vinculado à sua conta.",
  "contact.linkHow.telegram": "Envie {code} para o bot do Telegram da plataforma em até {n} minutos. O chat que enviar fica vinculado à sua conta.",
  "contact.linkSent": "Já enviei — conferir de novo",
  "contact.emailDigest": "E-mail diário com as decisões que esperam por mim",
  "contact.emailDigestHint": "Enviado uma vez por dia, só quando houver algo esperando.",
  "contact.preferences": "Preferências",
  "contact.digestHour": "Horário do e-mail diário",
  "contact.timezone": "Fuso horário",
  "contact.timezoneHint": "Por exemplo America/Sao_Paulo.",
  "contact.timezoneInvalid": "Use um fuso horário como America/Sao_Paulo.",
  "contact.locale": "Idioma das mensagens",
  "contact.saved": "Contatos salvos.",
  "channels.title": "Canais de notificação",
  "channels.intro": "Conexões da organização usadas para enviar perguntas e receber respostas. Senhas e tokens nunca são mostrados de novo depois de salvos.",
  "channels.state": "Situação",
  "channels.saved": "{channel} salvo.",
  "channels.configured": "Configurado",
  "channels.notConfigured": "Não configurado",
  "channels.about.whatsapp": "WhatsApp pelo seu servidor GOWA.",
  "channels.about.telegram": "Telegram por um bot criado no BotFather.",
  "channels.about.email": "E-mail pelo seu servidor SMTP (resumo diário).",
  "channels.secret.whatsapp": "Senha do GOWA",
  "channels.secret.telegram": "Token do bot",
  "channels.secret.email": "Senha do SMTP",
  "channels.secretHint": "Deixe em branco para manter o atual.",
  "channels.webhookSecret": "Segredo do webhook",
  "channels.webhookSecretLength": "Use pelo menos {n} caracteres.",
  "channels.inboundUrl": "Endereço para mensagens recebidas",
  "channels.inboundPending": "Salve o canal com as credenciais para gerar o endereço.",
  "channels.inboundHint.whatsapp": "Cole como URL de webhook no GOWA, com o mesmo segredo do webhook.",
  "channels.inboundHint.telegram": "Registre como webhook do bot (setWebhook) com o mesmo token secreto.",
  "channels.copy": "Copiar",
  "channels.copied": "Endereço copiado.",
  "channelField.base_url": "Endereço do GOWA",
  "channelField.username": "Usuário",
  "channelField.api_url": "Endereço da API do Telegram",
  "channelField.host": "Servidor SMTP",
  "channelField.port": "Porta",
  "channelField.from": "Remetente (From)",
  "channelField.security": "Segurança da conexão",
  "channelField.security.tls": "TLS",
  "channelField.security.starttls": "STARTTLS",
  "channelField.security.none": "Nenhuma (não recomendado)",
  "notifications.title": "Minhas notificações",
  "notifications.emptyTitle": "Nenhuma notificação ainda",
  "notifications.emptyBody": "As perguntas e os resumos enviados para você aparecem aqui.",
  "notifications.col.when": "Quando",
  "notifications.col.channel": "Canal",
  "notifications.col.subject": "Assunto",
  "notifications.col.state": "Situação",
  "notifications.kind.approval_request": "Pedido de decisão",
  "notifications.kind.daily_digest": "Resumo diário",
  "notifications.kind.support_reply": "Resposta do suporte",
  "notifications.state.queued": "Na fila",
  "notifications.state.sent": "Enviada",
  "notifications.state.failed": "Falhou",
  "notifications.state.skipped": "Não enviada",

  // Entrada
  "auth.magicExpiredTitle": "Este link expirou ou já foi usado",
  "auth.magicExpiredBody": "Sem problema: entre normalmente aqui embaixo. Os links do e-mail diário funcionam uma vez só e por tempo limitado.",
  "mfa.verifyCodeSubtitle": "Digite o código de 6 dígitos do seu aplicativo autenticador para continuar.",
};
